import { Card, IconWrapper, Select } from "@caprikorn/dynamo-ui";
import { classNames } from "@caprikorn/dynamo-utils";
import { type FC, useId } from "react";
import { Controller, useWatch } from "react-hook-form";

import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { CircleCheck, LayerTriple } from "@icons";
import type {
  Contract,
  CustomerAllByGroup,
} from "@services/apiEcms/apiEcms.types";
import { useGetContracts } from "@services/apiEcms/useGetContracts";
import { useGetCustomer } from "@services/apiEcms/useGetCustomer";
import { useGetCustomersAllByGroup } from "@services/apiEcms/useGetCustomersAllByGroup";
import { useGetGroup } from "@services/apiEcms/useGetGroup";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./ContractCard.module.css";
import type { ContractCardProps } from "./ContractCard.types";
import type { AddPodFormData } from "../AddPodForm/AddPodForm.types";
import { getIsFilled } from "../AddPodForm/AddPodForm.utils";
import formClasses from "../form.module.css";

const contractFields: Array<keyof AddPodFormData> = ["client", "contract"];

export const ContractCard: FC<ContractCardProps> = ({
  errors,
  control,
  setValue,
}) => {
  const { t } = useCustomTranslation(["main", "common"]);
  const cardId = useId();

  const { currentGroup } = useGroupReferences();
  const { data: groupData } = useGetGroup({
    groupId: currentGroup,
  });
  const isAdmin = groupData?.isAdmin;

  const [client, contract] = useWatch({
    control,
    name: ["client", "contract"],
  });

  const isFilled = getIsFilled(contractFields, errors, {
    contract,
    client,
  });

  const { data: customersByGroupData } = useGetCustomersAllByGroup({
    enabled: isAdmin ?? false,
  });
  const { data: contractsByCustomer, isPending } = useGetContracts({
    pageNumber: 1,
    elementsPerPage: 999,
    customerId: client,
  });

  const { data: customerData } = useGetCustomer(client, {
    enabled: isAdmin === false,
  });

  const foundContractsAmount = contractsByCustomer?.contracts.length || 0;

  return (
    <Card aria-labelledby={cardId} as="section" className={formClasses.card}>
      <h3
        className={classNames(formClasses.titleContainer, "font-title-md")}
        id={cardId}
      >
        {isFilled ? (
          <IconWrapper
            className={formClasses.successIcon}
            role="presentation"
            size="lg"
            src={CircleCheck}
          />
        ) : (
          <IconWrapper role="presentation" size="md" src={LayerTriple} />
        )}
        {t("addPodDrawer.contract.title")}
      </h3>
      <div className={classes.formContainer}>
        <div className={classes.inputContainer}>
          <Controller
            control={control}
            name="client"
            render={({ field, fieldState }) => (
              <Select
                disabled={!isAdmin}
                label={t("addPodDrawer.contract.fields.customer")}
                onBlur={field.onBlur}
                onChange={(event) => {
                  field.onChange(event);
                  setValue("contract", "");
                }}
                size="lg"
                value={client !== "" ? client : null}
              >
                <Select.Trigger
                  className={formClasses.dropdown}
                  data-testid="clientSelector"
                  hasError={Boolean(fieldState.error)}
                  hint={
                    fieldState.error?.message &&
                    t(fieldState.error.message, { ns: "common" })
                  }
                />
                <Select.Dropdown data-testid="DropDown-List">
                  <Select.SearchInput
                    placeholder={t("addPodDrawer.contract.placeholder")}
                  />
                  <Select.Options>
                    {isAdmin
                      ? customersByGroupData?.map(
                          (option: CustomerAllByGroup) => (
                            <Select.Option
                              key={option.customerId}
                              label={`${option.customerName} - ${option.customerId}`}
                              value={option.customerId}
                            />
                          ),
                        )
                      : customerData && (
                          <Select.Option
                            key={customerData.customerId}
                            label={`${customerData.companyName} - ${customerData.customerId}`}
                            value={customerData.customerId}
                          />
                        )}
                  </Select.Options>
                </Select.Dropdown>
              </Select>
            )}
            rules={{ required: "form.error.required" }}
          />
        </div>
        <div className={classes.inputContainer}>
          <Controller
            control={control}
            name="contract"
            render={({ field, fieldState }) => (
              <Select
                {...field}
                disabled={!client || !isAdmin}
                isLoading={isPending}
                label={t("addPodDrawer.contract.fields.contract")}
                size="lg"
                value={field.value || null}
              >
                <Select.Trigger
                  className={formClasses.dropdown}
                  data-testid="contractSelector"
                  hasError={Boolean(fieldState.error)}
                  hint={
                    isPending
                      ? t("loading", { ns: "common" })
                      : fieldState.error?.message &&
                        t(fieldState.error.message, { ns: "common" })
                  }
                />
                <Select.Dropdown data-testid="DropDown-List">
                  {foundContractsAmount > 0 && (
                    <Select.SearchInput
                      placeholder={t("addPodDrawer.contract.placeholder")}
                    />
                  )}
                  <Select.Options>
                    {foundContractsAmount > 0 ? (
                      contractsByCustomer?.contracts?.map(
                        (option: Contract) => (
                          <Select.Option
                            key={option.contractInformation.contractId}
                            label={`${option.contractInformation.contractName} - ${option.contractInformation.contractId}`}
                            value={option.contractInformation.contractId}
                          />
                        ),
                      )
                    ) : (
                      <p>{t("addPodDrawer.contract.noContractFound")}</p>
                    )}
                  </Select.Options>
                </Select.Dropdown>
              </Select>
            )}
            rules={{ required: "form.error.required" }}
          />
        </div>
      </div>
    </Card>
  );
};
